import React, { useEffect, useState } from 'react'
import { Check, Copy, Eye, EyeOff, Loader2, X } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useToast } from "@/components/ui/use-toast"
import yaml from 'js-yaml';
import Link from 'next/link'; 
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { OAUTH_PROVIDERS, getAllScopesForProvider } from "@/lib/oauth-providers";
import { ServerConfigurationManager } from "./ServerConfigurationManager";
import { ServerConfiguration, ServerVariable } from "@/lib/api-config";

// Helper function to get current provider's default scopes (excluding mandatory)
const getCurrentProviderDefaultScopes = (providerKey?: string) => {
  if (!providerKey || !OAUTH_PROVIDERS[providerKey]) {
    return OAUTH_PROVIDERS.others.defaultScopes;
  }
  return OAUTH_PROVIDERS[providerKey].defaultScopes;
};

// Helper function to get current provider's mandatory scopes
const getCurrentProviderMandatoryScopes = (providerKey?: string) => {
  if (!providerKey || !OAUTH_PROVIDERS[providerKey]) {
    return OAUTH_PROVIDERS.others.defaultMandatoryScopes;
  }
  return OAUTH_PROVIDERS[providerKey].defaultMandatoryScopes;
};

interface DeployData {
  projectName: string;
  path: string;
  branchName: string;
  installationId: string;
  githubAppName: string;
  githubAppId: string;
}

interface DeployModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  deployData: DeployData;
  onSuccess: () => void;
}

interface APISettings {
  team: string;
  project: string;
  MIAproject: string;
  MIAprojectShort: string;
  apiPortal: boolean;
  mcp: boolean;
  mcpUrl?: string;
  throttlingRate?: number;
  throttlingBurst?: number;
  Quota: number;
  QuotaPeriod: string;
  userManagement: boolean;
  userGroup: string;
  useApiKeys: boolean;
  useCustomOAuth?: boolean;
  oauthSettings: {
    clientId: string;
    clientSecret: string;
    domain: string;
    provider?: string;
    scopes?: string[];
    allowedCallbackUrls: string[];
    allowedLogoutUrls: string[];
    allowedOriginUrls: string[];
  };
  serverConfigurations?: ServerConfiguration[];
}

interface OpenAPISpec {
  info?: {
    version?: string;
    [key: string]: any;
  };
  swagger?: string;
  openapi?: string;
  servers?: Array<{
    url: string;
    description?: string;
  }>;
  [key: string]: any;
}

function generateCode() {
  const numbers = '0123456789';
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  return numbers.charAt(Math.floor(Math.random() * numbers.length)) + 
         Array.from({ length: 2 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
}

const generateDefaultProjectName = (projectName: string) => 
  projectName.replace(/[^a-zA-Z0-9]/g, '') + generateCode();

const buildApiEndpointRoute = (baseUrl: string, serverDescription?: string, serverUrl?: string): string => {
  const envKeywords = ['prod', 'production', 'dev', 'development', 'qa', 'test', 'testing', 'staging', 'sandbox', 'uat', 'beta', 'alpha'];
  
  if (serverDescription) {
    const descriptionLower = serverDescription.toLowerCase();
    
    for (const keyword of envKeywords) {
      if (descriptionLower.includes(keyword)) {
        return `${keyword}`;
      }
    }
    
    const urlSafePath = descriptionLower
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    return `${urlSafePath}`;
  }
  
  if (serverUrl) {
    const urlLower = serverUrl.toLowerCase();
    
    for (const keyword of envKeywords) {
      if (urlLower.includes(keyword)) {
        return `${keyword}`;
      }
    }
    
    const hash = Array.from(serverUrl)
      .reduce((acc, char, i) => acc + char.charCodeAt(0) * (i + 1), 0)
      .toString(36)
      .substring(0, 3);
    
    return `${hash}`;
  }
  
  return baseUrl;
};

const DeployModal: React.FC<DeployModalProps> = ({ 
  isOpen, 
  onOpenChange, 
  deployData, 
  onSuccess 
}) => {
  console.log('🚀 DeployModal component is rendering!')
  console.log('📅 Timestamp:', new Date().toISOString())
  console.log('🔧 Modal props:', { isOpen, deployData })
  const [previouslyDeployed, setPreviouslyDeployed] = useState(false);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [spec, setSpec] = useState<any>(null);
  const [apiVersion, setApiVersion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedStates, setCopiedStates] = useState({
    callback: false,
    logout: false,
    origin: false
  });

  const [settings, setSettings] = useState<APISettings>({
    team: '',
    project: '',
    MIAproject: '',
    MIAprojectShort: '',
    apiPortal: true,
    mcp: true,
    mcpUrl: '',
    throttlingRate: undefined,
    throttlingBurst: undefined,
    Quota: 1000,
    QuotaPeriod: 'day',
    userManagement: true,
    userGroup: 'usergroup1',
    useApiKeys: false,
    useCustomOAuth: false,
    oauthSettings: {
      clientId: '',
      clientSecret: '',
      domain: 'https://',
      provider: 'others',
      scopes: [],
      allowedCallbackUrls: [],
      allowedLogoutUrls: [],
      allowedOriginUrls: []
    },
    serverConfigurations: []
  });

  const [oauthUrls, setOauthUrls] = useState({
    callback: '',
    logout: '',
    origin: ''
  });

  const URL_TIMEOUT = 2000;
  const [isValidating, setIsValidating] = useState(false);
  const [isProjectValid, setIsProjectValid] = useState(true);

  const { toast } = useToast();

  // Debug logging for OAuth settings
  useEffect(() => {
    if (settings?.useCustomOAuth) {
      console.log('🔐 DeployModal OAuth Custom settings are enabled!')
      console.log('📋 Current OAuth settings:', settings.oauthSettings)
    }
  }, [settings?.useCustomOAuth, settings?.oauthSettings])

  const getAuthUrl = (settings: APISettings) => {
    const teamPart = settings.team ? `-${settings.team}` : '';
    return `https://myInstantAPI${teamPart}-${settings.userGroup}.auth.us-east-1.amazoncognito.com/oauth2/idpresponse`;
  };

  const validateProject = async (projectName: string) => {
    setIsValidating(true);
    try {
      const response = await fetch('/api/MIAproject', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectName }),
      });
      const data = await response.json();
      setIsProjectValid(!data.MIAproject);
    } catch (error) {
      console.error('Failed to validate project:', error);
      setIsProjectValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleServerConfigurationChange = (serverIndex: number, configuration: ServerConfiguration) => {
    setSettings(prev => {
      const currentConfigs = prev.serverConfigurations || [];
      const updatedConfigs = [...currentConfigs];
      
      // Ensure the array is large enough
      while (updatedConfigs.length <= serverIndex) {
        updatedConfigs.push({ serverUrl: '', variables: [] });
      }
      
      updatedConfigs[serverIndex] = configuration;
      
      return {
        ...prev,
        serverConfigurations: updatedConfigs
      };
    });
  };

  // Function to extract API key security schemes from OpenAPI spec
  const extractApiKeySecuritySchemes = (spec: OpenAPISpec): ServerVariable[] => {
    if (spec.components?.securitySchemes) {
      const securitySchemes = spec.components.securitySchemes;
      
      for (const [schemeName, scheme] of Object.entries(securitySchemes)) {
        const typedScheme = scheme as any;
        if (typedScheme.type === 'apiKey') {
          const headerName = typedScheme.name || schemeName;
          const location = typedScheme.in || 'header';
          
          if (location === 'header') {
            return [{
              name: headerName,
              value: 'TYPE_APIKEY_HERE',
              type: 'header'
            }];
          } else if (location === 'query') {
            return [{
              name: headerName,
              value: 'TYPE_APIKEY_HERE',
              type: 'parameter'
            }];
          }
        }
      }
    }
    
    return [];
  };

  // Function to auto-populate server configurations with API key variables
  const autoPopulateApiKeys = (spec: OpenAPISpec, serverIndex: number) => {
    const apiKeyVariables = extractApiKeySecuritySchemes(spec);
    
    if (apiKeyVariables.length > 0) {
      // Use a callback to get the current state at the time of execution
      setSettings(prev => {
        const currentConfig = prev.serverConfigurations?.[serverIndex];
        const existingVariables = currentConfig?.variables || [];
        
        // Check if API key variables are already present
        const hasApiKeyVariables = apiKeyVariables.some(apiKeyVar => 
          existingVariables.some(existingVar => 
            existingVar.name === apiKeyVar.name && existingVar.type === apiKeyVar.type
          )
        );
        
        if (!hasApiKeyVariables) {
          const updatedVariables = [...existingVariables, ...apiKeyVariables];
          const updatedConfigs = [...(prev.serverConfigurations || [])];
          
          // Ensure the array is large enough
          while (updatedConfigs.length <= serverIndex) {
            updatedConfigs.push({ serverUrl: '', variables: [] });
          }
          
          updatedConfigs[serverIndex] = {
            serverUrl: spec.servers?.[serverIndex]?.url || '',
            variables: updatedVariables
          };
          
          return {
            ...prev,
            serverConfigurations: updatedConfigs
          };
        }
        
        return prev; // No changes needed
      });
    }
  };

  // Fetch config and OpenAPI spec when modal opens
  useEffect(() => {
    if (!isOpen || !deployData) return;

    const fetchConfigAndSpec = async () => {
      setIsFetching(true);
      try {
        // Fetch OpenAPI spec
        const specResponse = await fetch(`/api/openapi?repo=${encodeURIComponent(deployData.projectName)}&file=${encodeURIComponent(deployData.path)}`);
        if (specResponse.ok) {
          const specData = await specResponse.json();
          let parsedSpec;
          
          if (typeof specData.spec === 'string') {
            try {
              parsedSpec = yaml.load(specData.spec) as OpenAPISpec;
            } catch (err) {
              console.error("Error parsing YAML spec:", err);
              parsedSpec = {};
            }
          } else {
            parsedSpec = specData.spec as OpenAPISpec;
          }
          
          setSpec(parsedSpec);
          
          let version = '1.0.0';
          if (parsedSpec && parsedSpec.info && parsedSpec.info.version) {
            version = parsedSpec.info.version;
          }
          setApiVersion(version);
          
          // Auto-populate API key variables for each server
          if (parsedSpec.servers && parsedSpec.servers.length > 0) {
            parsedSpec.servers.forEach((_, index) => {
              setTimeout(() => autoPopulateApiKeys(parsedSpec, index), 100);
            });
          }
        }

        // Fetch current config
        const configResponse = await fetch(`/api/api_setup?${new URLSearchParams({
          projectName: deployData.projectName,
          path: deployData.path.replace(/[^a-zA-Z0-9]/g, '_'),
          branchName: deployData.branchName,
          installationId: deployData.installationId,
          githubAppName: deployData.githubAppName,
          githubAppId: deployData.githubAppId
        })}`);

        const apiData = await configResponse.json();
        setApiResponse(apiData);
        
        if (configResponse.ok && apiData.data?.config) {
          const existingSettings = apiData.data.config.settings;
          if (existingSettings) {
            setPreviouslyDeployed(true);
            // Load existing settings including OAuth if it exists
            setSettings({
              ...existingSettings,
              team: existingSettings.team || apiData.data?.userName || '',
              // Preserve OAuth settings if they exist, otherwise use defaults
              useCustomOAuth: existingSettings.useCustomOAuth || false,
              oauthSettings: existingSettings.oauthSettings || {
                clientId: '',
                clientSecret: '',
                domain: 'https://',
                provider: 'others',
                scopes: [],
                allowedCallbackUrls: [],
                allowedLogoutUrls: [],
                allowedOriginUrls: []
              }
            });
          } else {
            setSettings(prev => ({
              ...prev,
              team: apiData.data?.userName || '',
              project: deployData.projectName,
              MIAproject: generateDefaultProjectName(deployData.projectName),
              MIAprojectShort: generateDefaultProjectName(deployData.projectName),
              // Reset OAuth settings to defaults
              useCustomOAuth: false,
              oauthSettings: {
                clientId: '',
                clientSecret: '',
                domain: 'https://',
                provider: 'others',
                scopes: [],
                allowedCallbackUrls: [],
                allowedLogoutUrls: [],
                allowedOriginUrls: []
              }
            }));
          }
        } else {
          setSettings(prev => ({
            ...prev,
            team: apiData.data?.userName || '',
            project: deployData.projectName,
            MIAproject: generateDefaultProjectName(deployData.projectName),
            MIAprojectShort: generateDefaultProjectName(deployData.projectName),
            // Reset OAuth settings to defaults
            useCustomOAuth: false,
            oauthSettings: {
              clientId: '',
              clientSecret: '',
              domain: 'https://',
              provider: 'others',
              scopes: [],
              allowedCallbackUrls: [],
              allowedLogoutUrls: [],
              allowedOriginUrls: []
            }
          }));
        }
      } catch (error) {
        console.error('Failed to fetch config:', error);
        setSettings(prev => ({
          ...prev,
          project: deployData.projectName,
          MIAproject: generateDefaultProjectName(deployData.projectName),
          MIAprojectShort: generateDefaultProjectName(deployData.projectName)
        }));
        toast({
          title: "Error",
          description: "Failed to load configuration, using default settings",
          variant: "destructive",
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchConfigAndSpec();
  }, [isOpen, deployData, toast]);

  // Update oauthUrls when settings change
  useEffect(() => {
    if (settings) {
      const { allowedCallbackUrls = [], allowedLogoutUrls = [], allowedOriginUrls = [] } = settings.oauthSettings || {};
      const authUrl = getAuthUrl(settings);
      setOauthUrls({
        callback: allowedCallbackUrls.join(', ') || authUrl,
        logout: allowedLogoutUrls.join(', ') || authUrl,
        origin: allowedOriginUrls.join(', ') || authUrl
      });
    }
  }, [settings]);

  // Function to check if there are any unchanged API keys
  const hasUnchangedApiKeys = () => {
    return settings.serverConfigurations?.some(config => 
      config.variables?.some(variable => variable.value === 'TYPE_APIKEY_HERE')
    ) || false;
  };

  const handleDeploy = async () => {
    if (!deployData) return;

    // Check for unchanged API keys before deploying
    if (hasUnchangedApiKeys()) {
      toast({
        title: "API Key Required",
        description: "In Target Servers, please replace 'TYPE_APIKEY_HERE' with your actual API key",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const sanitizedApiVersion = (apiVersion || '1.0.0').replace(/\./g, '--');
      const MIAprojectShort = `${settings.MIAproject.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
      const MIAprojectLong = `${settings.MIAproject.toLowerCase().replace(/[^a-z0-9]/g, '')}-ver-${sanitizedApiVersion}`;

      // Ensure we have all scopes without duplicates
      const currentScopes = settings.oauthSettings?.scopes || [];
      const provider = settings.oauthSettings?.provider ? OAUTH_PROVIDERS[settings.oauthSettings.provider] : null;
      const providerScopes = provider?.defaultScopes || [];
      const mandatoryScopes = provider?.defaultMandatoryScopes || OAUTH_PROVIDERS.others.defaultMandatoryScopes;
      // Remove duplicates by using a Set and preserving order
      const allScopes = Array.from(new Set([...mandatoryScopes, ...providerScopes, ...currentScopes]));

      const response = await fetch('/api/api_setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: apiResponse?.data?.userName || '',
          MIAproject: MIAprojectLong,
          MIAprojectShort: MIAprojectShort,
          projectName: deployData.projectName,
          path: deployData.path,
          settings: {
            ...settings,
            oauthSettings: {
              ...settings.oauthSettings,
              scopes: allScopes
            },
            team: apiResponse?.data?.userName?.split('@')[0] || '',
            project: deployData.projectName,
            throttlingRate: settings.throttlingRate || 0,
            throttlingBurst: settings.throttlingBurst || 0,
            MIAproject: MIAprojectLong,
            MIAprojectShort: MIAprojectShort,
          },
          branchName: deployData.branchName,
          installationId: deployData.installationId,
          githubAppName: deployData.githubAppName,
          githubAppId: deployData.githubAppId
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to deploy API');
      }

      onSuccess();
    } catch (error) {
      console.error('API deployment failed:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to deploy API",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90%] max-h-[90vh] overflow-hidden flex flex-col mx-auto">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Deploy API - {deployData.projectName}</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {isFetching ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex items-center space-x-2">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Loading configuration...</span>
              </div>
            </div>
          ) : (
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="auth">Authentication</TabsTrigger>
                <TabsTrigger value="targets">Target Servers</TabsTrigger>
                <TabsTrigger value="portal">Portal</TabsTrigger>
                <TabsTrigger value="throttling">Throttling & Quotas</TabsTrigger>
              </TabsList>
              
              <TabsContent value="general">
                <div className="space-y-6 py-4">
                  <div className="flex gap-2 items-end">
                    <div className="space-y-2 w-[70%]">
                      <Label htmlFor="miaproject" className="text-lg font-medium">Project name</Label>
                      <div className="relative">
                        <div className="flex items-center">
                          <Input
                            id="miaproject"
                            value={settings.MIAprojectShort}
                            onChange={(e) => {
                              const filteredValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                              setSettings(prev => ({
                                ...prev,
                                MIAproject: filteredValue,
                                MIAprojectShort: filteredValue
                              }));
                            }}
                            onBlur={(e) => validateProject(e.target.value)}
                            className="pr-32"
                          />
                          <span className="absolute right-10 text-gray-500 text-base">.myInstantAPI.com</span>
                          <div className="absolute right-2">
                            {isValidating ? (
                              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                            ) : isProjectValid ? (
                              <Check className="h-5 w-5 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center self-stretch mt-8 pb-2 px-1">
                      <span className="text-xl text-gray-500 font-normal">/</span>
                    </div>

                    <div className="space-y-2 flex-1">
                      <Label htmlFor="apiVersion" className="text-lg font-medium">API Version</Label>
                      <div className="relative">
                        <div 
                          className="bg-gray-100 px-3 py-2 rounded-md border border-input hover:bg-gray-200 transition-colors cursor-pointer flex items-center h-10"
                          onClick={() => {
                            const githubUrl = `https://github.com/${settings.team}/${deployData.projectName}/blob/main/${deployData.path}`;
                            window.open(githubUrl, '_blank');
                          }}
                        >
                          <span className="text-blue-600 underline font-sans text-[0.875rem] leading-[1.25rem]">
                            {apiVersion || '1.0.0'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <div className="space-y-2 flex-1">
                      <Label htmlFor="team">GitHub Team</Label>
                      <Input
                        id="team"
                        value={settings.team}
                        disabled
                        className="bg-gray-100"
                        placeholder={settings.team || apiResponse?.data?.userName}
                      />
                    </div>
                    <div className="space-y-2 flex-1">
                      <Label htmlFor="project">GitHub Project</Label>
                      <Input
                        id="project"
                        value={settings.project}
                        disabled
                        className="bg-gray-100"
                        placeholder={deployData.projectName}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="path">GitHub Path</Label>
                    <Input
                      id="path"
                      value={deployData.path}
                      disabled
                      className="bg-gray-100"
                      placeholder={deployData.path}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="mcp"
                      checked={settings.mcp}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        mcp: checked as boolean
                      }))}
                    />
                    <Label htmlFor="mcp">Enable MCP</Label>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="targets">
                <div className="space-y-4 py-4">
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/3">Stage</TableHead>
                          <TableHead className="w-1/3">Target Server URL</TableHead>
                          <TableHead className="w-1/3">Configuration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {spec && spec.servers && spec.servers.length > 0 ? (
                          spec.servers.map((server: { url: string; description?: string }, index: number) => {
                            const apiEndpointRoute = buildApiEndpointRoute(
                              `${settings.MIAprojectShort}.myInstantAPI.com/${apiVersion || '1.0.0'}`,
                              server.description,
                              server.url
                            );
                            
                            return (
                              <TableRow key={index}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <span>
                                      {`https://${settings.MIAprojectShort}.myInstantAPI.com/${apiVersion || '1.0.0'}/`}
                                      <Link 
                                        target="_blank" 
                                        className="text-blue-600 underline font-sans text-[0.875rem] leading-[1.25rem]" 
                                        href={`https://github.com/${settings.team}/${deployData.projectName}/blob/main/${deployData.path}`}
                                      >
                                        {apiEndpointRoute}
                                      </Link>
                                    </span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 hover:bg-gray-100"
                                      onClick={() => {
                                        navigator.clipboard.writeText(`https://${settings.MIAprojectShort}.myInstantAPI.com/${apiVersion || '1.0.0'}/${apiEndpointRoute}`);
                                        toast({
                                          title: "URL Copied",
                                          description: "API endpoint has been copied to clipboard",
                                        });
                                      }}
                                    >
                                      <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span>{server.url}</span>
                                </TableCell>
                                <TableCell>
                                  <ServerConfigurationManager
                                    serverUrl={server.url}
                                    serverIndex={index}
                                    configuration={settings.serverConfigurations?.[index]}
                                    onConfigurationChange={handleServerConfigurationChange}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground py-4">
                              No target servers defined in your{" "}
                              <Link 
                                target="_blank" 
                                className="text-blue-600 underline font-sans text-[0.875rem] leading-[1.25rem]" 
                                href={`https://github.com/${settings.team}/${deployData.projectName}/blob/main/${deployData.path}`}
                              >
                                OpenAPI specification
                              </Link>
                              . A backend will be generated at{" "}
                              <a 
                                href={`https://github.com/${settings.team}/${deployData.projectName}/blob/main/api_backend`} 
                                target="_blank" 
                                className="text-blue-600 underline hover:text-blue-800"
                                rel="noopener noreferrer"
                              >
                                {`github.com/${settings.team}/${deployData.projectName}/api_backend`}
                              </a>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="auth">
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="userGroup">UserGroup Name (your API dev portal users)</Label>
                    <Input
                      id="userGroup"
                      value={settings.userGroup}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        userGroup: e.target.value
                      }))}
                      placeholder="Enter user group name"
                    />
                  </div>

                  {/* API Key Authentication - Moved above OAuth */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="apiKey"
                      checked={settings.useApiKeys}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        useApiKeys: checked as boolean
                      }))}
                    />
                    <Label htmlFor="apiKey">
                      Enable API Key Authentication
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="userManagement"
                      checked={settings.userManagement}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          setOauthUrls({
                            callback: getAuthUrl(settings),
                            logout: getAuthUrl(settings),
                            origin: getAuthUrl(settings)
                          });
                        }
                        setSettings(prev => ({
                          ...prev,
                          userManagement: checked as boolean,
                          userGroup: 'usergroup1',
                          useCustomOAuth: checked ? prev.useCustomOAuth : false,
                          oauthSettings: checked ? prev.oauthSettings : {
                            clientId: '',
                            clientSecret: '',
                            domain: '',
                            allowedCallbackUrls: [],
                            allowedLogoutUrls: [],
                            allowedOriginUrls: []
                          }
                        }));
                      }}
                    />
                    <Label htmlFor="userManagement">Issue (oAuth) API token to these users</Label>
                  </div>

                  {settings.userManagement && (
                    <>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="useCustomOAuth"
                          checked={settings.useCustomOAuth}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              setOauthUrls({
                                callback: getAuthUrl(settings),
                                logout: getAuthUrl(settings),
                                origin: getAuthUrl(settings)
                              });
                            } else if (settings.oauthSettings) {
                              const { allowedCallbackUrls = [], allowedLogoutUrls = [], allowedOriginUrls = [] } = settings.oauthSettings;
                              setOauthUrls({
                                callback: allowedCallbackUrls.join(', ') || getAuthUrl(settings),
                                logout: allowedLogoutUrls.join(', ') || getAuthUrl(settings),
                                origin: allowedOriginUrls.join(', ') || getAuthUrl(settings)
                              });
                            }
                            setSettings(prev => ({
                              ...prev,
                              useCustomOAuth: checked as boolean,
                              oauthSettings: checked ? 
                                prev.oauthSettings || { 
                                  clientId: '', 
                                  clientSecret: '', 
                                  domain: 'https://',
                                  provider: 'others',
                                  scopes: OAUTH_PROVIDERS.others.defaultScopes,
                                  allowedCallbackUrls: [],
                                  allowedLogoutUrls: [],
                                  allowedOriginUrls: []
                                }
                                : prev.oauthSettings
                            }));
                          }}
                        />
                        <Label htmlFor="useCustomOAuth">Use my own OAuth Identity Provider (e.g. Auth0)</Label>
                      </div>

                      {settings.useCustomOAuth && (
                        <div className="grid grid-cols-2 gap-6 pl-6">
                          {/* Left Column - OAuth Inputs */}
                          <div className="space-y-4">
                          {/* OAuth Provider Dropdown */}
                          <div className="space-y-2">
                            <Label htmlFor="oauthProvider">OAuth Provider</Label>
                            <Select
                              value={settings.oauthSettings?.provider || 'others'}
                              onValueChange={(value) => {
                                const provider = OAUTH_PROVIDERS[value];
                                if (!provider) return;
                                
                                const domain = provider.domain;
                                const allScopes = getAllScopesForProvider(value);
                                
                                setSettings(prev => ({
                                  ...prev,
                                  oauthSettings: {
                                    ...prev.oauthSettings!,
                                    provider: value,
                                    domain,
                                    scopes: allScopes
                                  }
                                }));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select OAuth provider" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(OAUTH_PROVIDERS).map(([key, provider]) => (
                                  <SelectItem key={key} value={key}>
                                    {provider.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Identity Provider Domain */}
                          <div className="space-y-2">
                            <Label htmlFor="domain">Identity Provider Domain</Label>
                            <Input
                              id="domain"
                              value={settings.oauthSettings?.domain || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                const domain = value.startsWith('http://') || value.startsWith('https://') ? 
                                  value : 
                                  value ? `https://${value}` : '';
                                setSettings(prev => ({
                                  ...prev,
                                  oauthSettings: {
                                    ...prev.oauthSettings!,
                                    domain,
                                    provider: prev.oauthSettings?.provider || 'others',
                                    scopes: prev.oauthSettings?.scopes || [],
                                    clientId: prev.oauthSettings?.clientId || '',
                                    clientSecret: prev.oauthSettings?.clientSecret || '',
                                    allowedCallbackUrls: prev.oauthSettings?.allowedCallbackUrls || [],
                                    allowedLogoutUrls: prev.oauthSettings?.allowedLogoutUrls || [],
                                    allowedOriginUrls: prev.oauthSettings?.allowedOriginUrls || []
                                  }
                                }));
                              }}
                              placeholder="Enter your OAuth Domain"
                            />
                          </div>



                          <div className="space-y-2">
                            <Label htmlFor="clientId">Identity Provider Client ID</Label>
                            <Input
                              id="clientId"
                              value={settings.oauthSettings?.clientId || ''}
                              onChange={(e) => setSettings(prev => ({
                                ...prev,
                                oauthSettings: {
                                  ...prev.oauthSettings!,
                                  clientId: e.target.value,
                                  allowedCallbackUrls: prev.oauthSettings?.allowedCallbackUrls || [],
                                  allowedLogoutUrls: prev.oauthSettings?.allowedLogoutUrls || [],
                                  allowedOriginUrls: prev.oauthSettings?.allowedOriginUrls || []
                                }
                              }))}
                              placeholder="Enter your OAuth Client ID"
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="clientSecret">Identity Provider Client Secret</Label>
                            <div className="relative">
                              <Input
                                id="clientSecret"
                                type={showSecret ? "text" : "password"}
                                value={settings.oauthSettings?.clientSecret || ''}
                                onChange={(e) => setSettings(prev => ({
                                  ...prev,
                                  oauthSettings: {
                                    ...prev.oauthSettings!,
                                    clientSecret: e.target.value,
                                    allowedCallbackUrls: prev.oauthSettings?.allowedCallbackUrls || [],
                                    allowedLogoutUrls: prev.oauthSettings?.allowedLogoutUrls || [],
                                    allowedOriginUrls: prev.oauthSettings?.allowedOriginUrls || []
                                  }
                                }))}
                                placeholder="Enter your OAuth Client Secret"
                                className="pr-10"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-2 top-1/2 -translate-y-1/2"
                                onClick={() => setShowSecret(!showSecret)}
                              >
                                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                          </div>

                          {/* Authorized Scopes */}
                          <div className="space-y-2">
                            <Label>Authorized Scopes</Label>
                            <div className="rounded-md border">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Scope</TableHead>
                                    <TableHead className="w-20">Action</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {/* Default scopes - always present and non-removable */}
                                  {getCurrentProviderMandatoryScopes(settings.oauthSettings?.provider).map((mandatoryScope: string, index: number) => (
                                    <TableRow key={`default-${index}`}>
                                      <TableCell>
                                        <Input
                                          value={mandatoryScope}
                                          disabled
                                          className="bg-gray-100 text-gray-600"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <div className="text-xs text-gray-500 px-2">Default</div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  
                                  {/* Provider default scopes - editable and removable */}
                                  {getCurrentProviderDefaultScopes(settings.oauthSettings?.provider).map((providerScope: string, index: number) => (
                                    <TableRow key={`provider-${index}`}>
                                      <TableCell>
                                        <Input
                                          value={providerScope}
                                          onChange={(e) => {
                                            const currentScopes = settings.oauthSettings?.scopes || [];
                                            const mandatoryScopes = getCurrentProviderMandatoryScopes(settings.oauthSettings?.provider);
                                            const providerScopes = getCurrentProviderDefaultScopes(settings.oauthSettings?.provider);
                                            const customScopes = currentScopes.filter(scope => !mandatoryScopes.includes(scope) && !providerScopes.includes(scope));
                                            providerScopes[index] = e.target.value;
                                            const newScopes = [...mandatoryScopes, ...providerScopes, ...customScopes];
                                            setSettings(prev => ({
                                              ...prev,
                                              oauthSettings: {
                                                ...prev.oauthSettings!,
                                                scopes: newScopes
                                              }
                                            }));
                                          }}
                                          placeholder="Enter scope"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => {
                                            const currentScopes = settings.oauthSettings?.scopes || [];
                                            const mandatoryScopes = getCurrentProviderMandatoryScopes(settings.oauthSettings?.provider);
                                            const providerScopes = getCurrentProviderDefaultScopes(settings.oauthSettings?.provider);
                                            const customScopes = currentScopes.filter(scope => !mandatoryScopes.includes(scope) && !providerScopes.includes(scope));
                                            providerScopes.splice(index, 1);
                                            const newScopes = [...mandatoryScopes, ...providerScopes, ...customScopes];
                                            setSettings(prev => ({
                                              ...prev,
                                              oauthSettings: {
                                                ...prev.oauthSettings!,
                                                scopes: newScopes
                                              }
                                            }));
                                          }}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  
                                  {/* Custom scopes - removable */}
                                  {settings.oauthSettings?.scopes
                                    ?.filter(scope => !getCurrentProviderMandatoryScopes(settings.oauthSettings?.provider).includes(scope) && !getCurrentProviderDefaultScopes(settings.oauthSettings?.provider).includes(scope))
                                    ?.map((scope, index) => (
                                    <TableRow key={`custom-${index}`}>
                                      <TableCell>
                                        <Input
                                          value={scope}
                                          onChange={(e) => {
                                            // Ensure mandatory and provider scopes are always included
                                            const currentScopes = settings.oauthSettings?.scopes || [];
                                            const providerScopes = getCurrentProviderDefaultScopes(settings.oauthSettings?.provider);
                                            const mandatoryScopes = getCurrentProviderMandatoryScopes(settings.oauthSettings?.provider);
                                            const customScopes = currentScopes.filter(scope => !mandatoryScopes.includes(scope) && !providerScopes.includes(scope));
                                            customScopes[index] = e.target.value;
                                            const newScopes = [...mandatoryScopes, ...providerScopes, ...customScopes];
                                            setSettings(prev => ({
                                              ...prev,
                                              oauthSettings: {
                                                ...prev.oauthSettings!,
                                                scopes: newScopes
                                              }
                                            }));
                                          }}
                                          placeholder="Enter scope"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => {
                                            // Ensure mandatory and provider scopes are always included
                                            const currentScopes = settings.oauthSettings?.scopes || [];
                                            const providerScopes = getCurrentProviderDefaultScopes(settings.oauthSettings?.provider);
                                            const mandatoryScopes = getCurrentProviderMandatoryScopes(settings.oauthSettings?.provider);
                                            const customScopes = currentScopes.filter(scope => !mandatoryScopes.includes(scope) && !providerScopes.includes(scope));
                                            customScopes.splice(index, 1);
                                            const newScopes = [...mandatoryScopes, ...providerScopes, ...customScopes];
                                            setSettings(prev => ({
                                              ...prev,
                                              oauthSettings: {
                                                ...prev.oauthSettings!,
                                                scopes: newScopes
                                              }
                                            }));
                                          }}
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  <TableRow>
                                    <TableCell colSpan={2}>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          // Ensure mandatory and provider scopes are always included
                                          const currentScopes = settings.oauthSettings?.scopes || [];
                                          const providerScopes = getCurrentProviderDefaultScopes(settings.oauthSettings?.provider);
                                          const mandatoryScopes = getCurrentProviderMandatoryScopes(settings.oauthSettings?.provider);
                                          const customScopes = currentScopes.filter(scope => !mandatoryScopes.includes(scope) && !providerScopes.includes(scope));
                                          const newScopes = [...mandatoryScopes, ...providerScopes, ...customScopes, ''];
                                          setSettings(prev => ({
                                            ...prev,
                                            oauthSettings: {
                                              ...prev.oauthSettings!,
                                              scopes: newScopes
                                            }
                                          }));
                                        }}
                                      >
                                        Add Scope
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                          </div>

                          {/* Right Column - Warning and Instructions */}
                          <div className="space-y-4">
                            {/* Warning Box */}
                            <div className="space-y-2">
                              <Label>Important</Label>
                              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
                                <div className="flex">
                                  <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">
                                      Don&apos;t forget to add this authorized callback URL to your OAuth provider:
                                    </h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                      <div className="flex items-center gap-2">
                                        <code className="bg-yellow-100 px-2 py-1 rounded text-xs break-all flex-1">
                                          https://apiportal.myInstantAPI.com
                                        </code>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 flex-shrink-0"
                                          onClick={() => {
                                            const redirectUrl = `https://apiportal.myInstantAPI.com/?project=${settings.MIAprojectShort}&apiVersion=${apiVersion || '1.0.0'}`;
                                            navigator.clipboard.writeText(redirectUrl);
                                            toast({
                                              title: "URL Copied",
                                              description: "Redirect URL has been copied to clipboard",
                                            });
                                          }}
                                        >
                                          <Copy className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* GCP Setup Instructions */}
                            {settings.oauthSettings?.provider === 'google' && (
                              <div className="space-y-3">
                                <Label className="text-sm font-medium">Google Cloud Platform Setup</Label>
                                <div className="rounded-md border border-blue-200 bg-blue-50 p-4 space-y-3">
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-blue-800">Step 1: Go to Google Cloud Console</h4>
                                    <p className="text-xs text-blue-700">
                                      1. Visit <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">console.cloud.google.com</a><br/>
                                      2. Select your project or create a new one
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-blue-800">Step 2: Enable APIs</h4>
                                    <p className="text-xs text-blue-700">
                                      1. Go to <strong>APIs & Services</strong> → <strong>Library</strong><br/>
                                      2. Search for and enable: <strong>Google+ API</strong>, <strong>Gmail API</strong> (if using Gmail scopes)
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-blue-800">Step 3: Create OAuth 2.0 Credentials</h4>
                                    <p className="text-xs text-blue-700">
                                      1. Go to <strong>APIs & Services</strong> → <strong>Credentials</strong><br/>
                                      2. Click <strong>+ CREATE CREDENTIALS</strong> → <strong>OAuth 2.0 Client IDs</strong><br/>
                                      3. Choose <strong>Web application</strong> as application type
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-blue-800">Step 4: Configure OAuth Consent Screen</h4>
                                    <p className="text-xs text-blue-700">
                                      1. Go to <strong>APIs & Services</strong> → <strong>OAuth consent screen</strong><br/>
                                      2. Choose <strong>External</strong> user type<br/>
                                      3. Fill in app name, user support email, and developer contact
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-blue-800">Step 5: Add Authorized Redirect URIs</h4>
                                    <p className="text-xs text-blue-700">
                                      1. In your OAuth 2.0 Client ID settings<br/>
                                      2. Under <strong>Authorized redirect URIs</strong>, click <strong>+ ADD URI</strong><br/>
                                      3. Paste the callback URL above and click <strong>Save</strong>
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-blue-800">Step 6: Copy Credentials</h4>
                                    <p className="text-xs text-blue-700">
                                      1. Copy the <strong>Client ID</strong> and <strong>Client Secret</strong><br/>
                                      2. Paste them in the fields on the left
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Auth0 Setup Instructions */}
                            {settings.oauthSettings?.provider === 'auth0' && (
                              <div className="space-y-3">
                                <Label className="text-sm font-medium">Auth0 Setup</Label>
                                <div className="rounded-md border border-purple-200 bg-purple-50 p-4 space-y-3">
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-purple-800">Step 1: Create Auth0 Application</h4>
                                    <p className="text-xs text-purple-700">
                                      1. Go to your Auth0 Dashboard<br/>
                                      2. Navigate to <strong>Applications</strong> → <strong>Applications</strong><br/>
                                      3. Click <strong>+ Create Application</strong>
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-purple-800">Step 2: Configure Application</h4>
                                    <p className="text-xs text-purple-700">
                                      1. Choose <strong>Regular Web Application</strong><br/>
                                      2. Go to <strong>Settings</strong> tab
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-purple-800">Step 3: Add Callback URL</h4>
                                    <p className="text-xs text-purple-700">
                                      1. In <strong>Allowed Callback URLs</strong>, add the URL above<br/>
                                      2. In <strong>Allowed Logout URLs</strong>, add your logout URL<br/>
                                      3. In <strong>Allowed Web Origins</strong>, add your domain
                                    </p>
                                  </div>
                                  
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-purple-800">Step 4: Copy Credentials</h4>
                                    <p className="text-xs text-purple-700">
                                      1. Copy <strong>Domain</strong>, <strong>Client ID</strong>, and <strong>Client Secret</strong><br/>
                                      2. Paste them in the fields on the left
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Generic Instructions for Other Providers */}
                            {settings.oauthSettings?.provider && !['google', 'auth0'].includes(settings.oauthSettings.provider) && (
                              <div className="space-y-3">
                                <Label className="text-sm font-medium">OAuth Provider Setup</Label>
                                <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-gray-800">General Steps</h4>
                                    <p className="text-xs text-gray-700">
                                      1. Create an OAuth application in your provider&apos;s developer console<br/>
                                      2. Set the application type to <strong>Web Application</strong><br/>
                                      3. Add the callback URL above to <strong>Authorized Redirect URIs</strong><br/>
                                      4. Copy the <strong>Client ID</strong> and <strong>Client Secret</strong><br/>
                                      5. Paste them in the fields on the left
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}


                </div>
              </TabsContent>

              <TabsContent value="portal">
                <div className="space-y-4 py-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="apiPortal"
                      checked={settings.apiPortal}
                      onCheckedChange={(checked) => setSettings(prev => ({
                        ...prev,
                        apiPortal: checked as boolean
                      }))}
                    />
                    <Label htmlFor="apiPortal">Enable API Portal</Label>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="throttling">
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="throttlingRate">Throttling Rate (requests per second)</Label>
                      <Input
                        id="throttlingRate"
                        type="number"
                        value={settings.throttlingRate || ''}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          throttlingRate: parseInt(e.target.value) || undefined
                        }))}
                        placeholder="Requests per second"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="throttlingBurst">Throttling Burst</Label>
                      <Input
                        id="throttlingBurst"
                        type="number"
                        value={settings.throttlingBurst || ''}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          throttlingBurst: parseInt(e.target.value) || undefined
                        }))}
                        placeholder="Maximum burst size"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quota">Quota</Label>
                    <div className="flex items-start space-x-2">
                      <Input
                        id="quota"
                        type="number"
                        value={1000}
                        disabled
                        className="bg-gray-100"
                      />
                      <div className="w-40">
                        <Select
                          value="day"
                          disabled
                        >
                          <SelectTrigger id="quotaPeriod" className="bg-gray-100">
                            <SelectValue placeholder="Select period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="day">per day</SelectItem>
                            <SelectItem value="week">per week</SelectItem>
                            <SelectItem value="month">per month</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={() => {}}
                        variant="secondary"
                        className="whitespace-nowrap"
                      >
                        Upgrade
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>

        <div className="flex justify-end space-x-2 mt-6 pt-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeploy} disabled={isLoading || isFetching}>
            {isLoading ? (
              <div className="flex items-center">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Deploying...</span>
              </div>
            ) : (
              'Deploy API'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeployModal;